import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  positionCard: {
    background: "#e8eaf6", borderRadius: "8px", padding: "16px", textAlign: "center",
    marginBottom: "8px", border: "1px solid #c5cae9"
  },
  positionTitle: { fontSize: "12px", color: "#555", margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.5px" },
  positionName: { fontSize: "16px", fontWeight: "bold", color: "#1a237e", margin: "0 0 4px" },
  positionMeta: { fontSize: "11px", color: "#777", margin: 0 }
};

const POSITIONS = [
  "President",
  "Vice President",
  "Secretary General",
  "Treasurer",
  "Minister of Education",
  "Minister of Health",
  "Minister of Culture",
  "Minister of Finance",
  "PRO",
  "Sergeant at Arms"
];

const BLANK = { position: "", studentId: "", term: "", electedDate: "" };

class GuildManagement extends Component {
  state = {
    positions: [],
    students: [],
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null,
    usingFallback: false
  };

  componentDidMount() {
    this.fetchGuild();
    this.fetchStudents();
  }

  fetchGuild = async () => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/guild", { params: { limit: 50 } });
      const d = res.data.data || res.data;
      this.setState({ positions: Array.isArray(d) ? d : d.data || [], loading: false, usingFallback: false });
    } catch (e) {
      // Graceful fallback: show empty state, allow adding via form
      this.setState({ positions: [], loading: false, usingFallback: true });
    }
  };

  fetchStudents = async () => {
    try {
      const res = await api.get("/students", { params: { limit: 200, status: "active" } });
      const d = res.data.data || res.data;
      this.setState({ students: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });

  openEdit = item => {
    this.setState({
      open: true, editing: item.id, error: null,
      form: {
        position: item.position || "",
        studentId: item.studentId || item.student?.id || "",
        term: item.term || "",
        electedDate: item.electedDate ? item.electedDate.substring(0, 10) : ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.position || !form.studentId) {
      this.setState({ error: "Position and student are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editing) {
        await api.put(`/guild/${editing}`, form);
      } else {
        await api.post("/guild", form);
      }
      this.setState({ open: false, saving: false });
      this.fetchGuild();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save. The guild endpoint may not be available.", saving: false });
    }
  };

  handleDelete = async id => {
    if (!window.confirm("Remove this guild position record?")) return;
    try {
      await api.delete(`/guild/${id}`);
      this.fetchGuild();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { positions, students, loading, open, editing, form, saving, error, usingFallback } = this.state;

    // Build a map of position -> occupant for the leadership board
    const positionMap = {};
    positions.forEach(p => { positionMap[p.position] = p; });

    const getStudentName = p => {
      const student = students.find(s => s.id === (p.studentId || p.student?.id));
      return student ? `${student.firstName} ${student.lastName}` : (p.student?.firstName ? `${p.student.firstName} ${p.student.lastName}` : p.studentName || "—");
    };

    const columns = [
      {
        id: "position",
        header: "Position",
        accessorFn: p => p.position || "—",
        cell: info => <strong>{info.getValue()}</strong>
      },
      { id: "student", header: "Student", accessorFn: p => getStudentName(p) },
      { id: "term", header: "Term", accessorFn: p => p.term || "—" },
      {
        id: "electedDate",
        header: "Elected Date",
        accessorFn: p => (p.electedDate ? new Date(p.electedDate).toLocaleDateString("en-UG") : "—")
      },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const status = info.row.original.status || "active";
          return <Pill tone={status === "inactive" ? "danger" : "success"}>{status}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>
              <Icon>edit</Icon>
            </Button>
            <Button size="sm" color="danger" simple onClick={() => this.handleDelete(info.row.original.id)}>
              <Icon>delete</Icon>
            </Button>
          </div>
        )
      }
    ];

    const emptyState = (
      <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
        <Icon style={{ fontSize: "48px", color: "#e0e0e0", display: "block", margin: "0 auto 12px" }}>groups</Icon>
        <p>No guild positions recorded yet.</p>
        <Button size="sm" color="primary" onClick={this.openAdd}>
          Record First Position
        </Button>
      </div>
    );

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Student Guild Council</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    Leadership positions and current office holders
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={this.openAdd}>
                    <Icon style={{ marginRight: "4px" }}>group_add</Icon> Record Position
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {usingFallback && positions.length === 0 && (
                <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "6px", padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "#e65100" }}>
                  <Icon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
                  Guild endpoint not available. You can still record positions — they will be saved when the endpoint is ready.
                </div>
              )}

              {/* Leadership board — current term */}
              {positions.length > 0 && (
                <div style={{ marginBottom: "24px" }}>
                  <h5 style={{ color: "#1a237e", marginBottom: "12px" }}>Current Leadership</h5>
                  <GridContainer>
                    {POSITIONS.slice(0, 4).map(pos => {
                      const occ = positionMap[pos];
                      const student = occ && students.find(s => s.id === (occ.studentId || occ.student?.id));
                      const name = occ
                        ? (student ? `${student.firstName} ${student.lastName}` : occ.student?.firstName ? `${occ.student.firstName} ${occ.student.lastName}` : occ.studentName || "TBA")
                        : "Vacant";
                      return (
                        <GridItem xs={12} sm={6} md={3} key={pos}>
                          <div className={classes.positionCard}>
                            <p className={classes.positionTitle}>{pos}</p>
                            <p className={classes.positionName}>{name}</p>
                            {occ && <p className={classes.positionMeta}>{occ.term || ""}</p>}
                          </div>
                        </GridItem>
                      );
                    })}
                  </GridContainer>
                </div>
              )}

              <DataTable
                columns={columns}
                data={positions}
                isLoading={loading}
                emptyState={emptyState}
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Guild Position" : "Record Guild Position"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Position *</InputLabel>
              <Select value={form.position} onChange={this.handleChange("position")}>
                <MenuItem value="">— Select Position —</MenuItem>
                {POSITIONS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Student *</InputLabel>
              <Select value={form.studentId} onChange={this.handleChange("studentId")}>
                <MenuItem value="">— Select Student —</MenuItem>
                {students.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName} {s.studentNo ? `(${s.studentNo})` : ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <CustomInput labelText="Term (e.g. 2025/2026)" id="guildTerm" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.term, onChange: this.handleChange("term") }} />
            <CustomDatePicker labelText="Elected Date" id="guildElectedDate" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.electedDate, onChange: this.handleChange("electedDate") }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

GuildManagement.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(GuildManagement));
